import { type JSX } from 'react';

import { useHydrateEventsIntoStore } from 'queries/markets';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import { getSeriesImageUrl } from 'utils/images';

import SquareImage from './SquareImage';
import type { ImageFetchPriority, ImageLoading } from './types';

const NO_TICKERS: string[] = [];

interface SquareSeriesImageProps {
    seriesTicker?: string;
    eventTicker?: string;
    customImageUrl?: string;
    /**
     * The series' API-supplied artwork URL (`SeriesVo.image_url`, or the `image_url` the BFF/search
     * endpoints merge into `product_metadata`). It carries the backend's `?v=` cache buster, so pass it
     * whenever the payload you rendered from has it — it wins over the store because it is available
     * during SSR/first paint, before the effect-only store hydration.
     */
    seriesImageUrl?: string | null;
    alt?: string;
    size?: number;
    className?: string;
    loading?: ImageLoading;
    fetchPriority?: ImageFetchPriority;
}

const SquareSeriesImage = ({
    seriesTicker,
    eventTicker,
    customImageUrl,
    seriesImageUrl,
    alt = 'Series Image',
    size = 48,
    className,
    loading = 'lazy',
    fetchPriority = 'auto',
}: SquareSeriesImageProps): JSX.Element => {
    const event = useMarketEntitiesStore((state) => (eventTicker ? state.events[eventTicker] : undefined));
    // Only hydrate the single event when it isn't already in the store. Callers that render many rows
    // (e.g. navbar search) batch-hydrate the whole dropdown up front, so gating here restores the old
    // `event.loaded` guard and avoids one redundant `/events?tickers=<ticker>` request per row.
    const tickers = !customImageUrl && eventTicker && !event ? [eventTicker] : NO_TICKERS;
    useHydrateEventsIntoStore(tickers);

    // Callers on the event page pass an empty `seriesTicker` until the event resolves.
    const resolvedSeriesTicker = seriesTicker || event?.seriesTicker;
    // Read the series passively: no per-image series fetch (a list of N distinct series would fire N
    // unbatched requests). Surfaces where the version matters (event page, portfolio, hubs, brackets,
    // order panel) already batch-hydrate their series.
    const storeSeriesImageUrl = useMarketEntitiesStore((state) =>
        resolvedSeriesTicker ? state.series[resolvedSeriesTicker]?.imageUrl : undefined,
    );

    // Explicit image → the event's own override → the series artwork (API URL first, so a re-uploaded
    // image's `?v=` busts caches; ticker-derived last). Falling back to the series image (instead of
    // blank) while the event hydrates keeps a stable image at call sites that only pass series/event
    // tickers (portfolio & event rows, social posts) — the Zustand store is often empty there, so
    // waiting on the redundant `/events` fetch previously left the image blank, permanently so on a
    // slow or failed request.
    const imageSrc =
        customImageUrl ||
        event?.customImageUrl ||
        getSeriesImageUrl({
            seriesTicker: resolvedSeriesTicker,
            seriesImageUrl: seriesImageUrl ?? storeSeriesImageUrl,
        });

    return (
        <SquareImage
            src={imageSrc}
            alt={alt}
            size={size}
            className={className}
            loading={loading}
            fetchPriority={fetchPriority}
        />
    );
};

export default SquareSeriesImage;
